import { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import {
  Warning, Package, Truck, ClipboardText, Users, ArrowRight, CheckCircle, XCircle,
  Robot, PaperPlaneRight, SpinnerGap, Sparkle, Lightning, ArrowClockwise, Shield,
  X, EnvelopeSimple, Buildings,
} from '@phosphor-icons/react';
import api from '../api';
import { useLocationContext } from '../contexts/LocationContext';

const MONO = { fontFamily: "'JetBrains Mono', monospace" };
const COLORS = ['hsl(197,100%,48%)', 'hsl(12,90%,55%)', 'hsl(200,80%,55%)', 'hsl(43,96%,56%)', 'hsl(280,65%,60%)'];

// ── Draft Order Card ──────────────────────────────────────────────────────────
function DraftOrderCard({ draft, onConfirm, onCancel }) {
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const r = await api.put(`/orders/${draft.draft_id}/confirm-draft`);
      setConfirmed(true);
      onConfirm && onConfirm(r.data);
    } catch (e) {
      console.error(e);
      setConfirming(false);
    }
  };

  const handleCancel = async () => {
    setCancelled(true);
    try { await api.delete(`/orders/${draft.draft_id}`); } catch (_) {}
    onCancel && onCancel();
  };

  if (confirmed) return (
    <div data-testid="draft-confirmed-card"
      style={{ padding: '10px 14px', background: 'hsl(142,50%,6%)', border: '1px solid hsl(142,60%,25%)',
        ...MONO, fontSize: 11, color: 'hsl(142,70%,55%)', display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
      <CheckCircle size={14} weight="fill" />
      {draft.order_no} confirmed — now active
    </div>
  );

  if (cancelled) return (
    <div style={{ padding: '8px 12px', background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,15%)',
      ...MONO, fontSize: 10, color: 'hsl(0,0%,40%)', marginTop: 8 }}>
      Draft cancelled.
    </div>
  );

  return (
    <div data-testid="draft-order-card"
      style={{ border: '1px solid hsl(43,80%,30%)', background: 'hsl(43,40%,5%)', overflow: 'hidden', marginTop: 8 }}>
      <div style={{ padding: '8px 12px', background: 'hsl(43,60%,8%)',
        borderBottom: '1px solid hsl(43,60%,20%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Warning size={12} color="hsl(43,96%,56%)" weight="fill" />
          <span style={{ ...MONO, fontSize: 9, color: 'hsl(43,96%,56%)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>
            DRAFT ORDER — AWAITING CONFIRMATION
          </span>
        </div>
        <span style={{ ...MONO, fontSize: 10, color: 'hsl(43,96%,65%)', fontWeight: 700 }}>{draft.order_no}</span>
      </div>
      <div style={{ padding: '10px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
        {[
          ['Customer', draft.customer_name],
          ['Product', `${draft.product_name}${draft.product_sku ? ` (${draft.product_sku})` : ''}`],
          ['Quantity', (draft.quantity || 0).toLocaleString('en-IN')],
          ['Unit Price', `₹${(draft.unit_price || 0).toLocaleString('en-IN')}`],
          ['Total Value', `₹${(draft.total_value || 0).toLocaleString('en-IN')}`],
          ['Delivery', draft.promise_date || '—'],
          ['Priority', (draft.priority || 'medium').toUpperCase()],
        ].map(([k, v]) => (
          <div key={k}>
            <div style={{ ...MONO, fontSize: 8, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{k}</div>
            <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,85%)', marginTop: 1 }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: '10px 12px', borderTop: '1px solid hsl(43,40%,10%)', display: 'flex', gap: 8 }}>
        <button data-testid="confirm-draft-btn" onClick={handleConfirm} disabled={confirming}
          style={{ flex: 1, padding: '7px 0', background: confirming ? 'hsl(142,40%,15%)' : 'hsl(142,60%,20%)',
            border: '1px solid hsl(142,60%,35%)', color: 'hsl(142,70%,60%)',
            cursor: confirming ? 'not-allowed' : 'pointer', ...MONO, fontSize: 10,
            textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, transition: 'all 0.15s' }}>
          {confirming ? 'Confirming...' : 'Confirm Order'}
        </button>
        <button data-testid="cancel-draft-btn" onClick={handleCancel}
          style={{ padding: '7px 16px', background: 'transparent',
            border: '1px solid hsl(0,60%,30%)', color: 'hsl(0,70%,55%)',
            cursor: 'pointer', ...MONO, fontSize: 10, transition: 'all 0.15s' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ── Paste Email Overlay ───────────────────────────────────────────────────────
function PasteEmailOverlay({ onSubmit, onClose }) {
  const [text, setText] = useState('');
  return (
    <div style={{ position: 'absolute', inset: 0, background: 'hsl(0,0%,4%)', zIndex: 20,
      display: 'flex', flexDirection: 'column', padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ ...MONO, fontSize: 10, color: 'hsl(197,100%,48%)', textTransform: 'uppercase',
          letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: 6 }}>
          <EnvelopeSimple size={13} /> Paste Client Email / Text
        </div>
        <button onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,45%)', display: 'flex' }}>
          <X size={13} />
        </button>
      </div>
      <textarea
        data-testid="paste-email-textarea"
        autoFocus
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={"Paste email or any text here...\n\nExample:\nHi, we'd like to order 5000 Black Chisel Whiteboard Markers @ ₹40/piece. Please deliver by 15th May 2026.\n\nRegards,\nRajesh - Tech Supplies Ltd"}
        style={{ flex: 1, background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
          color: 'hsl(0,0%,80%)', padding: 12, resize: 'none', outline: 'none',
          ...MONO, fontSize: 10, lineHeight: 1.7, marginBottom: 10 }}
      />
      <button
        data-testid="parse-email-btn"
        onClick={() => { if (text.trim()) { onSubmit(text.trim()); } }}
        disabled={!text.trim()}
        style={{ padding: '9px 0', background: text.trim() ? 'hsl(197,100%,48%)' : 'hsl(0,0%,12%)',
          border: 'none', color: text.trim() ? '#000' : 'hsl(0,0%,35%)', ...MONO, fontSize: 11,
          fontWeight: 700, cursor: text.trim() ? 'pointer' : 'not-allowed',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all 0.15s' }}>
        <ArrowRight size={13} weight="bold" /> Parse & Create Order
      </button>
    </div>
  );
}

// ── Small reusable components ─────────────────────────────────────────────────
function MetricCard({ label, value, sub, accent, icon: Icon, testId }) {
  return (
    <div data-testid={testId} className="metric-card animate-fadeInUp" style={{ flex: 1, minWidth: 160 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{label}</div>
        {Icon && <Icon size={16} color={accent || 'hsl(0,0%,40%)'} />}
      </div>
      <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 28, fontWeight: 700, color: accent || 'hsl(0,0%,95%)', letterSpacing: '-0.02em' }}>{value}</div>
      {sub && <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,45%)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function AlertCard({ alert, onRead }) {
  const color = alert.severity === 'critical' ? 'hsl(0,84%,60%)' :
    alert.severity === 'warning' ? 'hsl(43,96%,56%)' : 'hsl(200,80%,55%)';
  return (
    <div data-testid={`alert-${alert.alert_type}`}
      style={{ borderLeft: `3px solid ${color}`, padding: '10px 14px',
        background: 'hsl(0,0%,5%)', marginBottom: 8, opacity: alert.is_read ? 0.5 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ ...MONO, fontSize: 11, color: 'hsl(0,0%,90%)', fontWeight: 600, marginBottom: 3 }}>{alert.title}</div>
          <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,55%)', lineHeight: 1.5 }}>{alert.message}</div>
          {alert.action && (
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 4, color: color, fontSize: 10, ...MONO }}>
              <ArrowRight size={10} />{alert.action}
            </div>
          )}
        </div>
        {!alert.is_read && (
          <button onClick={() => onRead(alert.id)} data-testid="mark-read-btn"
            style={{ background: 'transparent', border: '1px solid hsl(0,0%,18%)', padding: '2px 8px',
              color: 'hsl(0,0%,50%)', fontSize: 9, ...MONO, cursor: 'pointer',
              whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            MARK READ
          </button>
        )}
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    'in_production': { label: 'IN PROD', cls: 'chip-info' },
    'confirmed': { label: 'CONFIRMED', cls: 'chip-active' },
    'pending': { label: 'PENDING', cls: 'chip-neutral' },
    'ready_to_dispatch': { label: 'READY', cls: 'chip-warning' },
    'dispatched': { label: 'DISPATCHED', cls: 'chip-active' },
    'cancelled': { label: 'CANCELLED', cls: 'chip-danger' },
  };
  const { label, cls } = map[status] || { label: status?.toUpperCase(), cls: 'chip-neutral' };
  return <span className={cls}>{label}</span>;
}

// ── Main ControlTower Component ───────────────────────────────────────────────
export default function ControlTower() {
  const { activeLocation } = useLocationContext();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // AI chat state
  const [showAI, setShowAI] = useState(false);
  const [aiMessages, setAiMessages] = useState([
    {
      role: 'assistant',
      content: "Hi, I'm ARIA — your ERP assistant with live data access.\n\nI can query orders, inventory, quality, dispatch, finance, and production in real-time. I can also **create sales orders** from emails or descriptions.\n\nTry: \"How many open orders?\" or click the envelope icon to paste a client email.",
      tools: [],
    }
  ]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [showPasteEmail, setShowPasteEmail] = useState(false);
  const chatEndRef = useRef(null);

  // Smart alerts state
  const [smartAlerts, setSmartAlerts] = useState([]);
  const [smartScanTime, setSmartScanTime] = useState(null);
  const [scanning, setScanning] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const locParam = activeLocation ? `?location_code=${activeLocation.code}` : '';
        const [s, a, o] = await Promise.all([
          api.get(`/dashboard/stats${locParam}`),
          api.get('/dashboard/alerts'),
          api.get('/orders'),
        ]);
        setStats(s.data);
        setAlerts(a.data.filter(x => !x.is_read));
        setOrders(o.data.slice(0, 6));
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [activeLocation]);

  // Auto-scroll chat on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);

  const handleReadAlert = async (id) => {
    await api.put(`/alerts/${id}/read`);
    setAlerts(prev => prev.filter(a => a.id !== id));
  };

  const loadSmartAlerts = useCallback(async () => {
    try {
      const { data } = await api.get('/ai/alerts');
      setSmartAlerts(data.alerts || []);
      setSmartScanTime(data.scanned_at);
      if (data.scanning) {
        setScanning(true);
      } else {
        setScanning(false);
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      }
    } catch (e) {}
  }, []);

  const runSmartScan = async () => {
    if (scanning) return;
    setScanning(true);
    try {
      await api.post('/ai/smart-scan');
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(loadSmartAlerts, 3000);
    } catch (e) {
      console.error('Smart scan start failed:', e);
      setScanning(false);
    }
  };

  // Extract draft from tool_results (if AI called create_draft_order)
  const extractDraftFromResults = (toolResults) => {
    if (!toolResults) return null;
    const draftResult = toolResults.find(t => t.tool === 'create_draft_order');
    if (draftResult && draftResult.result && draftResult.result.draft_id) {
      return draftResult.result;
    }
    return null;
  };

  const sendMessage = async (messageText) => {
    if (!messageText.trim()) return;
    const userMsg = messageText.trim();
    setAiInput('');
    setShowPasteEmail(false);

    const historySnapshot = aiMessages.filter(m => m.content !== '__thinking__');
    setAiMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setAiLoading(true);
    setAiMessages(prev => [...prev, { role: 'assistant', content: '__thinking__', tools: [] }]);

    try {
      const { data } = await api.post('/ai/chat', {
        message: userMsg,
        history: historySnapshot.slice(-8),
      });

      const draft = extractDraftFromResults(data.tool_results);
      setAiMessages(prev => {
        const without = prev.filter(m => m.content !== '__thinking__');
        return [...without, {
          role: 'assistant',
          content: data.response,
          tools: data.tools_called || [],
          draft: draft || null,
        }];
      });
    } catch {
      setAiMessages(prev => {
        const without = prev.filter(m => m.content !== '__thinking__');
        return [...without, {
          role: 'assistant',
          content: 'Sorry, the AI service is currently unavailable. Please try again.',
          tools: [],
        }];
      });
    }
    setAiLoading(false);
  };

  const sendAI = async (e) => {
    e.preventDefault();
    await sendMessage(aiInput);
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
      <div style={{ color: 'hsl(197,100%,48%)', ...MONO, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.2em' }}>
        Loading...
      </div>
    </div>
  );

  const fmt = (n) => n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : `₹${(n / 1000).toFixed(0)}K`;

  return (
    <div data-testid="control-tower">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 22, fontWeight: 700,
          color: 'hsl(0,0%,98%)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
          Control Tower
        </h1>
        <div style={{ ...MONO, fontSize: 11, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
          ARIA Forge ERP · Live Operational Summary
        </div>
      </div>

      {/* Metric Row */}
      <div className="metrics-row" style={{ marginBottom: 1 }}>
        <MetricCard testId="metric-receivables" label="Total Receivables" icon={Users}
          value={fmt(stats?.total_receivables || 0)} sub="Outstanding balance" accent="hsl(43,96%,56%)" />
        <MetricCard testId="metric-open-orders" label="Open Orders" icon={ClipboardText}
          value={stats?.open_orders || 0} sub="Pending + Confirmed + In Prod" accent="hsl(197,100%,48%)" />
        <MetricCard testId="metric-in-production" label="In Production" icon={Package}
          value={stats?.in_production || 0} sub="Active work orders" accent="hsl(200,80%,55%)" />
        <MetricCard testId="metric-qc-holds" label="QC Holds" icon={Warning}
          value={stats?.qc_holds || 0} sub="Batches awaiting release" accent="hsl(0,84%,60%)" />
        <MetricCard testId="metric-ready-dispatch" label="Ready to Dispatch" icon={Truck}
          value={stats?.ready_dispatch || 0} sub="Pending shipments" accent="hsl(12,90%,55%)" />
      </div>

      {/* Charts + Alerts row */}
      <div className="charts-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 360px', gap: 1, marginBottom: 1, marginTop: 1 }}>
        {/* Revenue Chart */}
        <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: 20 }}>
          <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>
            Revenue · Last 6 Months
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={stats?.monthly_data || []}>
              <XAxis dataKey="month" tick={{ fill: 'hsl(0,0%,45%)', ...MONO, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={v => `₹${(v / 1000).toFixed(0)}K`}
                tick={{ fill: 'hsl(0,0%,40%)', ...MONO, fontSize: 9 }} axisLine={false} tickLine={false} width={50} />
              <Tooltip contentStyle={{ background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,18%)', ...MONO, fontSize: 11 }}
                formatter={v => [`₹${(v / 1000).toFixed(1)}K`, 'Revenue']} />
              <Bar dataKey="revenue" fill="hsl(197,100%,48%)" radius={0} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Order Status Pie */}
        <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: 20 }}>
          <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>
            Order Status Distribution
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <ResponsiveContainer width={140} height={140}>
              <PieChart>
                <Pie data={stats?.order_distribution || []} dataKey="count" cx="50%" cy="50%"
                  innerRadius={40} outerRadius={65} paddingAngle={2}>
                  {(stats?.order_distribution || []).map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1 }}>
              {(stats?.order_distribution || []).map((item, i) => (
                <div key={item.status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 8, height: 8, background: COLORS[i % COLORS.length] }} />
                    <span style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,60%)' }}>{item.status}</span>
                  </div>
                  <span style={{ ...MONO, fontSize: 11, color: 'hsl(0,0%,85%)', fontWeight: 600 }}>{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Exception Alerts Panel */}
        <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', padding: 16, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              Exception Alerts
            </div>
            {alerts.length > 0 && <span className="chip-danger">{alerts.length} UNREAD</span>}
          </div>
          <div style={{ flex: 1, overflowY: 'auto', maxHeight: 260 }}>
            {alerts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: 'hsl(0,0%,40%)', ...MONO, fontSize: 11 }}>
                <CheckCircle size={24} color="hsl(197,100%,48%)" style={{ marginBottom: 8 }} />
                <div>All clear</div>
              </div>
            ) : (
              alerts.map(a => <AlertCard key={a.id} alert={a} onRead={handleReadAlert} />)
            )}
          </div>
        </div>
      </div>

      {/* Location Breakdown — shown to global roles when All Locations is active */}
      {(stats?.location_breakdown || []).length > 0 && (
        <div style={{ marginBottom: 1, background: 'hsl(0,0%,4%)', border: '1px solid hsl(0,0%,14%)' }}>
          <div style={{ padding: '10px 16px', borderBottom: '1px solid hsl(0,0%,12%)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Buildings size={13} color="hsl(0,0%,45%)" />
            <span style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,40%)', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Location Breakdown
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px 90px', padding: '8px 16px', borderBottom: '1px solid hsl(0,0%,10%)', gap: 12 }}>
            {['Location', 'In Prod', 'QC Holds', 'Dispatch'].map(h => (
              <div key={h} style={{ ...MONO, fontSize: 8, color: 'hsl(0,0%,35%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>{h}</div>
            ))}
          </div>
          {(stats.location_breakdown).map(loc => {
            const typeColors = { factory: 'hsl(197,100%,48%)', warehouse: 'hsl(43,96%,56%)', office: 'hsl(142,70%,45%)', branch: 'hsl(280,60%,60%)' };
            const color = typeColors[loc.type] || 'hsl(0,0%,55%)';
            return (
              <div key={loc.code} style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px 90px', padding: '10px 16px', borderBottom: '1px solid hsl(0,0%,8%)', gap: 12, alignItems: 'center' }}>
                <div>
                  <div style={{ ...MONO, fontSize: 11, color, fontWeight: 600 }}>{loc.name}</div>
                  <div style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,38%)', marginTop: 2 }}>{loc.code} · {loc.type}</div>
                </div>
                <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, fontWeight: 700, color: loc.in_production > 0 ? 'hsl(197,100%,48%)' : 'hsl(0,0%,40%)' }}>{loc.in_production}</div>
                <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, fontWeight: 700, color: loc.qc_holds > 0 ? 'hsl(0,84%,60%)' : 'hsl(0,0%,40%)' }}>{loc.qc_holds}</div>
                <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 18, fontWeight: 700, color: loc.dispatch_ready > 0 ? 'hsl(142,70%,45%)' : 'hsl(0,0%,40%)' }}>{loc.dispatch_ready}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* AI Smart Alerts Panel */}
      <div style={{ marginBottom: 1, background: 'hsl(0,0%,4%)', border: '1px solid hsl(197,60%,18%)' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid hsl(0,0%,12%)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'hsl(197,80%,6%)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Sparkle size={14} color="hsl(197,100%,48%)" weight="fill" />
            <span style={{ ...MONO, fontSize: 10, fontWeight: 700, color: 'hsl(197,100%,48%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
              AI Smart Alerts
            </span>
            <span style={{ fontSize: 8, background: 'hsl(197,60%,10%)', color: 'hsl(197,100%,48%)',
              padding: '2px 6px', ...MONO, textTransform: 'uppercase' }}>
              ARIA-IQ
            </span>
            {smartScanTime && (
              <span style={{ fontSize: 9, color: 'hsl(0,0%,35%)', ...MONO }}>
                Last scan: {new Date(smartScanTime).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
              </span>
            )}
          </div>
          <button data-testid="run-smart-scan-btn" onClick={runSmartScan} disabled={scanning}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px',
              background: scanning ? 'hsl(0,0%,10%)' : 'hsl(197,100%,48%)',
              border: 'none', color: '#000', cursor: scanning ? 'wait' : 'pointer',
              ...MONO, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', transition: 'background 0.15s' }}>
            {scanning
              ? <><SpinnerGap size={10} style={{ animation: 'spin 0.6s linear infinite' }} /> Scanning...</>
              : <><ArrowClockwise size={10} weight="bold" /> Run Scan</>
            }
          </button>
        </div>

        {smartAlerts.length === 0 ? (
          <div style={{ padding: '20px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <Shield size={20} color="hsl(0,0%,25%)" />
            <div>
              <div style={{ ...MONO, fontSize: 11, color: 'hsl(0,0%,50%)' }}>No scan results yet</div>
              <div style={{ ...MONO, fontSize: 9, color: 'hsl(0,0%,30%)', marginTop: 2 }}>
                Click "Run Scan" to analyse your ERP data for anomalies and risks
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 0 }}>
            {smartAlerts.map((alert, i) => {
              const sev = alert.severity || 'info';
              const sevColor = sev === 'critical' ? 'hsl(0,84%,60%)' : sev === 'warning' ? 'hsl(43,96%,56%)' : 'hsl(197,100%,48%)';
              const sevBg = sev === 'critical' ? 'hsl(0,60%,8%)' : sev === 'warning' ? 'hsl(43,60%,7%)' : 'hsl(197,60%,6%)';
              return (
                <div key={i} data-testid={`smart-alert-${i}`}
                  style={{ padding: '12px 16px', borderLeft: `3px solid ${sevColor}`,
                    borderBottom: '1px solid hsl(0,0%,10%)', borderRight: '1px solid hsl(0,0%,10%)', background: sevBg }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <span style={{ fontSize: 8, padding: '2px 6px', background: `${sevColor}22`, color: sevColor,
                      ...MONO, textTransform: 'uppercase', letterSpacing: '0.08em', border: `1px solid ${sevColor}44` }}>
                      {sev}
                    </span>
                    <span style={{ fontSize: 8, color: 'hsl(0,0%,40%)', ...MONO, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {alert.module}
                    </span>
                  </div>
                  <div style={{ ...MONO, fontSize: 11, fontWeight: 700, color: 'hsl(0,0%,88%)', marginBottom: 4, lineHeight: 1.3 }}>
                    {alert.title}
                  </div>
                  <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,55%)', marginBottom: 6, lineHeight: 1.4 }}>
                    {alert.description}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
                    <Lightning size={10} color={sevColor} weight="fill" style={{ marginTop: 2, flexShrink: 0 }} />
                    <span style={{ ...MONO, fontSize: 9, color: sevColor, lineHeight: 1.4 }}>{alert.action}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Orders Table */}
      <div style={{ background: 'hsl(0,0%,5%)', border: '1px solid hsl(0,0%,14%)', marginTop: 1 }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid hsl(0,0%,12%)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ ...MONO, fontSize: 10, color: 'hsl(0,0%,50%)', textTransform: 'uppercase', letterSpacing: '0.12em' }}>
            Recent Sales Orders
          </div>
        </div>
        <div className="table-scroll">
          <table className="erp-table" data-testid="orders-table">
            <thead>
              <tr>
                <th>Order No</th><th>Customer</th><th>Product</th><th>Qty</th>
                <th>Value</th><th>Promise Date</th><th>Priority</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(o => {
                const days = Math.ceil((new Date(o.promise_date) - new Date()) / 86400000);
                const isAtRisk = days <= 3 && ['pending', 'confirmed', 'in_production'].includes(o.status);
                return (
                  <tr key={o.id} data-testid={`order-row-${o.order_no}`}>
                    <td style={{ color: 'hsl(197,100%,48%)', fontWeight: 600 }}>{o.order_no}</td>
                    <td>{o.customer_name}</td>
                    <td style={{ color: 'hsl(0,0%,70%)' }}>{o.product_sku}</td>
                    <td>{o.quantity?.toLocaleString()}</td>
                    <td>₹{(o.total_value / 1000).toFixed(0)}K</td>
                    <td style={{ color: isAtRisk ? 'hsl(0,84%,60%)' : 'hsl(0,0%,70%)' }}>
                      {new Date(o.promise_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      {isAtRisk && <span style={{ marginLeft: 6, fontSize: 9, color: 'hsl(0,84%,60%)' }}>AT RISK</span>}
                    </td>
                    <td>
                      <span className={o.priority === 'high' ? 'chip-danger' : o.priority === 'medium' ? 'chip-warning' : 'chip-neutral'}>
                        {o.priority?.toUpperCase()}
                      </span>
                    </td>
                    <td><StatusChip status={o.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Assistant FAB */}
      <button
        data-testid="ai-assistant-btn"
        onClick={() => setShowAI(v => !v)}
        style={{
          position: 'fixed', bottom: 80, right: 28, width: 48, height: 48,
          background: 'hsl(197,100%,48%)', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#000', boxShadow: '0 4px 20px rgba(0,180,255,0.35)',
          zIndex: 50, transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
        <Robot size={22} weight="bold" />
      </button>

      {/* AI Chat Panel */}
      {showAI && (
        <div data-testid="ai-panel" style={{
          position: 'fixed', bottom: 140, right: 28, width: 400, height: 520,
          background: 'hsl(0,0%,5%)', border: '1px solid hsl(197,60%,20%)',
          display: 'flex', flexDirection: 'column', zIndex: 50,
          boxShadow: '0 8px 40px rgba(0,180,255,0.15)',
        }}>
          {/* Panel Header */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid hsl(0,0%,12%)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            background: 'hsl(197,60%,8%)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Robot size={16} color="hsl(197,100%,48%)" />
              <span style={{ ...MONO, fontSize: 11, fontWeight: 700, color: 'hsl(197,100%,48%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                ARIA AI
              </span>
              <span style={{ fontSize: 8, background: 'hsl(197,60%,12%)', color: 'hsl(197,100%,48%)',
                padding: '2px 6px', ...MONO, textTransform: 'uppercase' }}>
                ARIA-IQ
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                data-testid="paste-email-toggle-btn"
                onClick={() => setShowPasteEmail(v => !v)}
                title="Paste client email to create order"
                style={{ background: showPasteEmail ? 'hsl(197,60%,18%)' : 'transparent',
                  border: `1px solid ${showPasteEmail ? 'hsl(197,80%,40%)' : 'hsl(0,0%,22%)'}`,
                  padding: '4px 8px', cursor: 'pointer', color: 'hsl(197,100%,48%)',
                  display: 'flex', alignItems: 'center', gap: 4, transition: 'all 0.15s' }}>
                <EnvelopeSimple size={12} />
                <span style={{ ...MONO, fontSize: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Email</span>
              </button>
              <button onClick={() => setShowAI(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,45%)', display: 'flex' }}>
                <XCircle size={16} />
              </button>
            </div>
          </div>

          {/* Relative wrapper for overlay */}
          <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

            {/* Paste Email Overlay */}
            {showPasteEmail && (
              <PasteEmailOverlay
                onSubmit={(text) => sendMessage(text)}
                onClose={() => setShowPasteEmail(false)}
              />
            )}

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {aiMessages.map((m, i) => (
                <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '92%' }}>
                  {/* Tool call badges */}
                  {m.role === 'assistant' && m.tools?.length > 0 && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                      {m.tools.map((t, ti) => (
                        <span key={ti} style={{
                          ...MONO, fontSize: 8, padding: '2px 6px',
                          background: 'hsl(197,50%,8%)', border: '1px solid hsl(197,60%,20%)',
                          color: 'hsl(197,100%,48%)', textTransform: 'uppercase', letterSpacing: '0.08em',
                        }}>
                          {t.tool.replace('query_', '').replace('get_erp_', 'erp ').replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Message bubble */}
                  <div style={{
                    padding: '8px 12px',
                    background: m.role === 'user' ? 'hsl(197,100%,48%)' : 'hsl(0,0%,8%)',
                    color: m.role === 'user' ? '#000' : 'hsl(0,0%,80%)',
                    ...MONO, fontSize: 11, lineHeight: 1.5,
                    border: m.role === 'assistant' ? '1px solid hsl(0,0%,14%)' : 'none',
                  }}>
                    {m.content === '__thinking__' ? (
                      <span style={{ color: 'hsl(0,0%,40%)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <SpinnerGap size={11} style={{ animation: 'spin 0.7s linear infinite' }} />
                        Querying live ERP data...
                      </span>
                    ) : m.role === 'assistant' ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          p: ({ children }) => <p style={{ margin: '2px 0', lineHeight: 1.5 }}>{children}</p>,
                          h2: ({ children }) => <strong style={{ display: 'block', color: 'hsl(0,0%,95%)', marginBottom: 4 }}>{children}</strong>,
                          h3: ({ children }) => <strong style={{ display: 'block', color: 'hsl(0,0%,85%)', marginBottom: 4, fontSize: 10 }}>{children}</strong>,
                          table: ({ children }) => <table style={{ borderCollapse: 'collapse', width: '100%', margin: '6px 0', fontSize: 10 }}>{children}</table>,
                          th: ({ children }) => <th style={{ border: '1px solid hsl(0,0%,20%)', padding: '4px 8px', textAlign: 'left', background: 'hsl(0,0%,10%)', color: 'hsl(197,100%,55%)' }}>{children}</th>,
                          td: ({ children }) => <td style={{ border: '1px solid hsl(0,0%,16%)', padding: '3px 8px', color: 'hsl(0,0%,80%)' }}>{children}</td>,
                          strong: ({ children }) => <strong style={{ color: 'hsl(0,0%,95%)' }}>{children}</strong>,
                          li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
                          ul: ({ children }) => <ul style={{ paddingLeft: 16, margin: '4px 0' }}>{children}</ul>,
                          code: ({ children }) => <code style={{ background: 'hsl(0,0%,10%)', padding: '1px 4px', color: 'hsl(197,100%,65%)' }}>{children}</code>,
                        }}>
                        {m.content}
                      </ReactMarkdown>
                    ) : m.content}
                  </div>

                  {/* Draft Order Card — shown inline after AI creates a draft */}
                  {m.role === 'assistant' && m.draft && (
                    <DraftOrderCard
                      draft={m.draft}
                      onConfirm={(result) => {
                        setAiMessages(prev => [...prev, {
                          role: 'assistant',
                          content: `Order **${result.order_no}** is now active and pending production.`,
                          tools: [],
                        }]);
                      }}
                      onCancel={() => {}}
                    />
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            {/* Quick prompts (only on first message) */}
            {aiMessages.length === 1 && (
              <div style={{ padding: '0 12px 8px', display: 'flex', gap: 6, flexWrap: 'wrap', flexShrink: 0 }}>
                {['How many open orders?', 'Low stock items?', 'QC holds today?', 'Unpaid invoices?'].map(q => (
                  <button key={q} onClick={() => setAiInput(q)}
                    style={{ fontSize: 9, padding: '3px 8px', background: 'hsl(197,40%,10%)',
                      border: '1px solid hsl(197,60%,18%)', color: 'hsl(197,100%,48%)',
                      ...MONO, cursor: 'pointer', letterSpacing: '0.05em' }}>
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form onSubmit={sendAI}
              style={{ padding: '10px 12px', borderTop: '1px solid hsl(0,0%,12%)', display: 'flex', gap: 8, flexShrink: 0 }}>
              <input
                data-testid="ai-input"
                value={aiInput}
                onChange={e => setAiInput(e.target.value)}
                placeholder="Ask about ERP data or describe an order..."
                disabled={aiLoading}
                style={{ flex: 1, background: 'hsl(0,0%,8%)', border: '1px solid hsl(0,0%,16%)',
                  padding: '7px 10px', color: 'hsl(0,0%,85%)', ...MONO, fontSize: 11, outline: 'none' }}
              />
              <button
                type="submit"
                data-testid="ai-send-btn"
                disabled={aiLoading || !aiInput.trim()}
                style={{ padding: '7px 12px', background: (aiInput.trim() && !aiLoading) ? 'hsl(197,100%,48%)' : 'hsl(0,0%,15%)',
                  border: 'none', cursor: (aiInput.trim() && !aiLoading) ? 'pointer' : 'not-allowed',
                  color: '#000', display: 'flex', alignItems: 'center', transition: 'background 0.15s' }}>
                <PaperPlaneRight size={14} weight="bold" />
              </button>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
